import { routeBoundaryCurve, routeEdge } from './edgeRouting';

export const DEFAULT_NODE_SIZE = Object.freeze({ width: 260, height: 110 });
export const DEFAULT_NEW_NODE_SIZE = Object.freeze({ width: 96, height: 44 });
export const DEFAULT_LAYOUT_GAP = Object.freeze({ horizontal: 80, vertical: 32 });
export const DEFAULT_COLLISION_PADDING = 16;

const MAX_FREE_SPACE_AXIS_CANDIDATES = 48;
const MAX_ROUTED_PLACEMENT_CANDIDATES = 8;

function finite(value, fallback) {
  return Number.isFinite(value) ? value : fallback;
}

export function getNodeSize(node) {
  const candidate = node?.layoutSize || node?.size || {};
  return {
    width: Math.max(1, finite(candidate.width, DEFAULT_NODE_SIZE.width)),
    height: Math.max(1, finite(candidate.height, DEFAULT_NODE_SIZE.height)),
  };
}

export function topLeftRectsOverlap(a, b, padding = 0) {
  return !(
    a.x + a.width + padding <= b.x ||
    b.x + b.width + padding <= a.x ||
    a.y + a.height + padding <= b.y ||
    b.y + b.height + padding <= a.y
  );
}

function nodeRect(node, nodeSizes = null) {
  const size = getRuntimeNodeSize(node, nodeSizes);
  return {
    x: finite(node?.position?.x, 0),
    y: finite(node?.position?.y, 0),
    ...size,
  };
}

function findParentId(nodeId, relationships) {
  return relationships.find((relationship) => relationship.childId === nodeId)?.parentId || null;
}

function getPinnedLayout(node, nodesById, relationships) {
  const existingLayout = node?.layout || {};
  const parentId = findParentId(node.id, relationships);
  const parent = nodesById.get(parentId);
  const nodeCenterX = finite(node?.position?.x, 0) + getNodeSize(node).width / 2;
  const parentCenterX = parent
    ? finite(parent.position?.x, 0) + getNodeSize(parent).width / 2
    : 0;
  const side = existingLayout.side === 'left' || existingLayout.side === 'right'
    ? existingLayout.side
    : (parent && nodeCenterX < parentCenterX ? 'left' : 'right');
  const siblingIds = relationships
    .filter((relationship) => relationship.parentId === parentId)
    .map((relationship) => relationship.childId)
    .sort((leftId, rightId) => {
      const left = nodesById.get(leftId);
      const right = nodesById.get(rightId);
      const leftOrder = finite(left?.layout?.order, Infinity);
      const rightOrder = finite(right?.layout?.order, Infinity);
      if (leftOrder !== rightOrder) return leftOrder - rightOrder;
      return finite(left?.position?.y, 0) - finite(right?.position?.y, 0)
        || String(leftId).localeCompare(String(rightId));
    });
  const inferredOrder = Math.max(0, siblingIds.indexOf(node.id));

  return {
    ...existingLayout,
    side,
    order: Math.max(0, finite(existingLayout.order, inferredOrder)),
    pinned: true,
  };
}

export function getSubtreeIds(nodeId, relationships) {
  if (!nodeId) return [];
  const safeRelationships = Array.isArray(relationships) ? relationships : [];
  const childrenByParent = new Map();

  safeRelationships.forEach((relationship) => {
    if (!relationship?.parentId || !relationship?.childId) return;
    const children = childrenByParent.get(relationship.parentId) || [];
    children.push(relationship.childId);
    childrenByParent.set(relationship.parentId, children);
  });

  childrenByParent.forEach((children) => {
    children.sort((left, right) => String(left).localeCompare(String(right)));
  });

  const result = [];
  const visited = new Set();
  const pending = [nodeId];

  while (pending.length > 0) {
    const current = pending.shift();
    if (!current || visited.has(current)) continue;
    visited.add(current);
    result.push(current);
    pending.push(...(childrenByParent.get(current) || []));
  }

  return result;
}

export function buildMoveSubtreeCommand({
  nodeId,
  targetPosition,
  nodes,
  relationships,
  pinRoot = false,
}) {
  const safeNodes = Array.isArray(nodes) ? nodes : [];
  const safeRelationships = Array.isArray(relationships) ? relationships : [];
  const nodesById = new Map(safeNodes.map((node) => [node.id, node]));
  const rootNode = nodesById.get(nodeId);
  if (!rootNode) throw new Error(`Node ${nodeId} does not exist`);
  if (!Number.isFinite(targetPosition?.x) || !Number.isFinite(targetPosition?.y)) {
    throw new Error('A finite target position is required');
  }

  const sourcePosition = {
    x: finite(rootNode.position?.x, 0),
    y: finite(rootNode.position?.y, 0),
  };
  const delta = {
    x: targetPosition.x - sourcePosition.x,
    y: targetPosition.y - sourcePosition.y,
  };
  const positions = getSubtreeIds(nodeId, safeRelationships)
    .map((id) => nodesById.get(id))
    .filter(Boolean)
    .map((node) => ({
      id: node.id,
      position: {
        x: finite(node.position?.x, 0) + delta.x,
        y: finite(node.position?.y, 0) + delta.y,
      },
      ...(pinRoot && node.id === nodeId ? {
        layout: getPinnedLayout(node, nodesById, safeRelationships),
      } : {}),
    }));

  return {
    type: 'move-subtree',
    nodeId,
    delta,
    positions,
  };
}

function getRuntimeNodeSize(node, nodeSizes) {
  const runtimeSize = nodeSizes instanceof Map
    ? nodeSizes.get(node.id)
    : nodeSizes?.[node.id];
  return runtimeSize ? getNodeSize({ ...node, layoutSize: runtimeSize }) : getNodeSize(node);
}

function commandHasCollision(command, nodes, nodeSizes, padding, offset) {
  const movingIds = new Set(command.positions.map((entry) => entry.id));
  const nodesById = new Map(nodes.map((node) => [node.id, node]));
  const stationaryRects = nodes
    .filter((node) => !movingIds.has(node.id))
    .map((node) => nodeRect(node, nodeSizes));

  return command.positions.some((entry) => {
    const node = nodesById.get(entry.id);
    if (!node) return false;
    const size = getRuntimeNodeSize(node, nodeSizes);
    const movedRect = {
      x: entry.position.x + offset.x,
      y: entry.position.y + offset.y,
      ...size,
    };
    return stationaryRects.some((rect) => topLeftRectsOverlap(movedRect, rect, padding));
  });
}

export function resolveMoveCommandCollisions(command, {
  nodes,
  nodeSizes = null,
  padding = DEFAULT_COLLISION_PADDING,
  step = 16,
  maxRings = 80,
} = {}) {
  if (!['move-subtree', 'reparent-subtree'].includes(command?.type) || !Array.isArray(command.positions)) {
    throw new Error('Invalid position command');
  }
  const safeNodes = Array.isArray(nodes) ? nodes : [];
  const safePadding = Math.max(0, finite(padding, DEFAULT_COLLISION_PADDING));
  const safeStep = Math.max(1, finite(step, 16));

  const collisionFreeAt = (offset) => !commandHasCollision(
    command,
    safeNodes,
    nodeSizes,
    safePadding,
    offset
  );

  let resolvedOffset = { x: 0, y: 0 };
  if (!collisionFreeAt(resolvedOffset)) {
    resolvedOffset = null;
    for (let ring = 1; ring <= maxRings && !resolvedOffset; ring += 1) {
      const distance = ring * safeStep;
      const candidates = [
        { x: 0, y: distance },
        { x: 0, y: -distance },
        { x: distance, y: 0 },
        { x: -distance, y: 0 },
      ];
      for (let index = 1; index < ring; index += 1) {
        const minor = index * safeStep;
        candidates.push(
          { x: minor, y: distance },
          { x: -minor, y: distance },
          { x: minor, y: -distance },
          { x: -minor, y: -distance },
          { x: distance, y: minor },
          { x: distance, y: -minor },
          { x: -distance, y: minor },
          { x: -distance, y: -minor },
        );
      }
      resolvedOffset = candidates.find(collisionFreeAt) || null;
    }
  }

  if (!resolvedOffset) throw new Error('Unable to resolve node collision');
  if (resolvedOffset.x === 0 && resolvedOffset.y === 0) return command;

  return {
    ...command,
    collisionResolved: true,
    collisionOffset: resolvedOffset,
    delta: {
      x: command.delta.x + resolvedOffset.x,
      y: command.delta.y + resolvedOffset.y,
    },
    positions: command.positions.map((entry) => ({
      ...entry,
      position: {
        x: entry.position.x + resolvedOffset.x,
        y: entry.position.y + resolvedOffset.y,
      },
    })),
  };
}

export function buildReparentSubtreeCommand({
  nodeId,
  newParentId,
  nodes,
  relationships,
  nodeSizes = null,
}) {
  const safeNodes = Array.isArray(nodes) ? nodes : [];
  const safeRelationships = Array.isArray(relationships) ? relationships : [];
  const nodesById = new Map(safeNodes.map((node) => [node.id, node]));
  const node = nodesById.get(nodeId);
  const newParent = nodesById.get(newParentId);
  if (!node) throw new Error(`Node ${nodeId} does not exist`);
  if (!newParent) throw new Error(`Parent node ${newParentId} does not exist`);
  if (nodeId === newParentId) throw new Error('A node cannot be its own parent');

  const previousParentId = findParentId(nodeId, safeRelationships);
  if (!previousParentId) throw new Error('The root node cannot be reparented');
  if (previousParentId === newParentId) throw new Error('Node already belongs to that parent');

  const subtreeIds = new Set(getSubtreeIds(nodeId, safeRelationships));
  if (subtreeIds.has(newParentId)) throw new Error('Reparenting would create a cycle');

  const stationaryNodes = safeNodes.filter((candidate) => !subtreeIds.has(candidate.id));
  const stationaryRelationships = safeRelationships.filter(
    (relationship) => !subtreeIds.has(relationship.parentId) && !subtreeIds.has(relationship.childId)
  );
  const targetLayout = planChildLayout({
    parentId: newParentId,
    nodes: stationaryNodes,
    relationships: stationaryRelationships,
    size: getRuntimeNodeSize(node, nodeSizes),
    nodeSizes,
  });
  const movement = buildMoveSubtreeCommand({
    nodeId,
    targetPosition: targetLayout.position,
    nodes: safeNodes,
    relationships: safeRelationships,
  });
  const command = {
    ...movement,
    type: 'reparent-subtree',
    positions: movement.positions.map((entry) => (
      entry.id === nodeId
        ? { ...entry, layout: targetLayout.layout }
        : entry
    )),
    previousParentId,
    newParentId,
    removeRelationship: { parentId: previousParentId, childId: nodeId },
    relationship: { parentId: newParentId, childId: nodeId },
  };

  return resolveMoveCommandCollisions(command, {
    nodes: safeNodes,
    nodeSizes,
    padding: DEFAULT_LAYOUT_GAP.vertical,
  });
}

function inferBranchSide(parent, nodesById, relationships, children) {
  if (parent?.layout?.side === 'left') return -1;
  if (parent?.layout?.side === 'right') return 1;
  if (children.length > 0) {
    const averageChildX = children.reduce(
      (sum, child) => sum + finite(child.position?.x, 0),
      0
    ) / children.length;
    return averageChildX < finite(parent.position?.x, 0) ? -1 : 1;
  }

  const grandparent = nodesById.get(findParentId(parent.id, relationships));
  if (grandparent) {
    return finite(parent.position?.x, 0) < finite(grandparent.position?.x, 0) ? -1 : 1;
  }

  return 1;
}

function centerOfNode(node, nodeSizes = null) {
  const size = getRuntimeNodeSize(node, nodeSizes);
  return {
    x: finite(node?.position?.x, 0) + size.width / 2,
    y: finite(node?.position?.y, 0) + size.height / 2,
  };
}

function normalizeVector(vector, fallback = { x: 1, y: 0 }) {
  const length = Math.hypot(vector?.x || 0, vector?.y || 0);
  if (length < 0.001) return fallback;
  return { x: vector.x / length, y: vector.y / length };
}

function crossProduct(a, b, c) {
  return (b.x - a.x) * (c.y - a.y) - (b.y - a.y) * (c.x - a.x);
}

function pointsEqual(a, b, epsilon = 0.001) {
  return Math.abs(a.x - b.x) <= epsilon && Math.abs(a.y - b.y) <= epsilon;
}

function pointOnSegment(point, start, end, epsilon = 0.001) {
  return Math.abs(crossProduct(start, end, point)) <= epsilon
    && point.x >= Math.min(start.x, end.x) - epsilon
    && point.x <= Math.max(start.x, end.x) + epsilon
    && point.y >= Math.min(start.y, end.y) - epsilon
    && point.y <= Math.max(start.y, end.y) + epsilon;
}

function segmentsCross(startA, endA, startB, endB) {
  const sharedOrigin = pointsEqual(startA, startB) || pointsEqual(startA, endB);
  if (sharedOrigin) {
    const otherEnd = pointsEqual(startA, startB) ? endB : startB;
    const collinear = Math.abs(crossProduct(startA, endA, otherEnd)) <= 0.001;
    if (!collinear) return false;

    const dot = (otherEnd.x - startA.x) * (endA.x - startA.x)
      + (otherEnd.y - startA.y) * (endA.y - startA.y);
    return dot > 0.001;
  }

  const a = crossProduct(startA, endA, startB);
  const b = crossProduct(startA, endA, endB);
  const c = crossProduct(startB, endB, startA);
  const d = crossProduct(startB, endB, endA);
  if (((a > 0 && b < 0) || (a < 0 && b > 0))
    && ((c > 0 && d < 0) || (c < 0 && d > 0))) {
    return true;
  }

  return (Math.abs(a) <= 0.001 && pointOnSegment(startB, startA, endA))
    || (Math.abs(b) <= 0.001 && pointOnSegment(endB, startA, endA))
    || (Math.abs(c) <= 0.001 && pointOnSegment(startA, startB, endB))
    || (Math.abs(d) <= 0.001 && pointOnSegment(endA, startB, endB));
}

function segmentCrossesRect(start, end, rect, padding = 0) {
  const expanded = {
    x: rect.x - padding,
    y: rect.y - padding,
    width: rect.width + padding * 2,
    height: rect.height + padding * 2,
  };
  const corners = [
    { x: expanded.x, y: expanded.y },
    { x: expanded.x + expanded.width, y: expanded.y },
    { x: expanded.x + expanded.width, y: expanded.y + expanded.height },
    { x: expanded.x, y: expanded.y + expanded.height },
  ];
  const isInside = (point) => (
    point.x >= expanded.x
    && point.x <= expanded.x + expanded.width
    && point.y >= expanded.y
    && point.y <= expanded.y + expanded.height
  );

  return isInside(start)
    || isInside(end)
    || corners.some((corner, index) => (
      segmentsCrossesWithoutSharedEndpoint(
        start,
        end,
        corner,
        corners[(index + 1) % corners.length]
      )
    ));
}

function segmentsCrossesWithoutSharedEndpoint(startA, endA, startB, endB) {
  const a = crossProduct(startA, endA, startB);
  const b = crossProduct(startA, endA, endB);
  const c = crossProduct(startB, endB, startA);
  const d = crossProduct(startB, endB, endA);

  if (((a > 0 && b < 0) || (a < 0 && b > 0))
    && ((c > 0 && d < 0) || (c < 0 && d > 0))) {
    return true;
  }
  return (Math.abs(a) <= 0.001 && pointOnSegment(startB, startA, endA))
    || (Math.abs(b) <= 0.001 && pointOnSegment(endB, startA, endA))
    || (Math.abs(c) <= 0.001 && pointOnSegment(startA, startB, endB))
    || (Math.abs(d) <= 0.001 && pointOnSegment(endA, startB, endB));
}

function inferChildDirection(parent, nodesById, relationships, children, nodeSizes = null) {
  const parentCenter = centerOfNode(parent, nodeSizes);
  const grandparent = nodesById.get(findParentId(parent.id, relationships));
  if (grandparent) {
    const grandparentCenter = centerOfNode(grandparent, nodeSizes);
    return normalizeVector({
      x: parentCenter.x - grandparentCenter.x,
      y: parentCenter.y - grandparentCenter.y,
    });
  }

  return { x: inferBranchSide(parent, nodesById, relationships, children), y: 0 };
}

function rectFitsUsableViewport(rect, bounds, obstacles) {
  if (!bounds) return true;
  return rect.x >= bounds.minX
    && rect.y >= bounds.minY
    && rect.x + rect.width <= bounds.maxX
    && rect.y + rect.height <= bounds.maxY
    && !obstacles.some((obstacle) => topLeftRectsOverlap(rect, obstacle));
}

function viewportOverflowDistance(rect, bounds) {
  if (!bounds) return 0;
  const dx = Math.max(
    bounds.minX - rect.x,
    0,
    rect.x + rect.width - bounds.maxX
  );
  const dy = Math.max(
    bounds.minY - rect.y,
    0,
    rect.y + rect.height - bounds.maxY
  );
  return Math.hypot(dx, dy);
}

function rectNearBounds(rect, bounds, margin) {
  if (!bounds) return true;
  return !(
    rect.x + rect.width < bounds.minX - margin
    || rect.x > bounds.maxX + margin
    || rect.y + rect.height < bounds.minY - margin
    || rect.y > bounds.maxY + margin
  );
}

function findAvailablePosition({
  parent,
  preferred,
  direction,
  size,
  nodes,
  relationships,
  gap,
  nodeSizes = null,
  usableViewport = null,
}) {
  const existingRects = nodes.map((node) => nodeRect(node, nodeSizes));
  const nodesById = new Map(nodes.map((node) => [node.id, node]));
  const viewportBounds = usableViewport?.bounds;
  const viewportObstacles = Array.isArray(usableViewport?.obstacles)
    ? usableViewport.obstacles
    : [];
  const hasUsableViewport = Boolean(
    viewportBounds
    && [
      viewportBounds.minX,
      viewportBounds.minY,
      viewportBounds.maxX,
      viewportBounds.maxY,
    ].every(Number.isFinite)
  );
  const usableBounds = hasUsableViewport ? viewportBounds : null;
  const parentCenter = centerOfNode(parent, nodeSizes);
  const parentSize = getRuntimeNodeSize(parent, nodeSizes);
  const unitDirection = normalizeVector(direction);
  const perpendicular = { x: -unitDirection.y, y: unitDirection.x };
  const supportDistance = (
    Math.abs(unitDirection.x) * (parentSize.width + size.width) / 2
    + Math.abs(unitDirection.y) * (parentSize.height + size.height) / 2
    + gap.horizontal
  );
  const preferredCenter = preferred
    ? { x: preferred.x + size.width / 2, y: preferred.y + size.height / 2 }
    : null;
  const preferredDistance = preferredCenter
    ? Math.max(
      supportDistance,
      (preferredCenter.x - parentCenter.x) * unitDirection.x
        + (preferredCenter.y - parentCenter.y) * unitDirection.y
    )
    : supportDistance;
  const perpendicularStep = (
    Math.abs(perpendicular.x) * (parentSize.width + size.width) / 2
    + Math.abs(perpendicular.y) * (parentSize.height + size.height) / 2
    + gap.vertical
  );
  const radialStep = Math.max(16, gap.vertical);
  const candidates = [];
  const candidateKeys = new Set();
  const addCandidate = (position, lane, radialRing = 0, orderPenalty = 0) => {
    if (!Number.isFinite(position?.x) || !Number.isFinite(position?.y)) return;
    const key = `${position.x.toFixed(3)}:${position.y.toFixed(3)}`;
    if (candidateKeys.has(key)) return;
    candidateKeys.add(key);
    const center = {
      x: position.x + size.width / 2,
      y: position.y + size.height / 2,
    };
    const along = (center.x - parentCenter.x) * unitDirection.x
      + (center.y - parentCenter.y) * unitDirection.y;
    const across = (center.x - parentCenter.x) * perpendicular.x
      + (center.y - parentCenter.y) * perpendicular.y;
    const distance = Math.hypot(center.x - parentCenter.x, center.y - parentCenter.y);
    const anglePenalty = Math.abs(Math.atan2(across, Math.max(0.001, along))) * 180;
    candidates.push({
      lane,
      radialRing,
      score: distance + anglePenalty + orderPenalty,
      position,
    });
  };

  for (let radialRing = 0; radialRing <= 32; radialRing += 1) {
    for (let lane = -16; lane <= 16; lane += 1) {
      const laneOffset = lane * perpendicularStep;
      const radialOffset = radialRing * radialStep;
      const center = {
        x: parentCenter.x + unitDirection.x * (preferredDistance + radialOffset)
          + perpendicular.x * laneOffset,
        y: parentCenter.y + unitDirection.y * (preferredDistance + radialOffset)
          + perpendicular.y * laneOffset,
      };
      addCandidate({
        x: center.x - size.width / 2,
        y: center.y - size.height / 2,
      }, lane, radialRing);
    }
  }

  if (usableViewport) {
    const alternateAngles = [30, -30, 60, -60, 90, -90, 120, -120, 150, -150, 180];
    alternateAngles.forEach((degrees, directionIndex) => {
      const radians = degrees * Math.PI / 180;
      const alternateDirection = {
        x: unitDirection.x * Math.cos(radians) - unitDirection.y * Math.sin(radians),
        y: unitDirection.x * Math.sin(radians) + unitDirection.y * Math.cos(radians),
      };
      const alternateSupportDistance = (
        Math.abs(alternateDirection.x) * (parentSize.width + size.width) / 2
        + Math.abs(alternateDirection.y) * (parentSize.height + size.height) / 2
        + gap.horizontal
      );
      for (let radialRing = 0; radialRing <= 12; radialRing += 1) {
        const distance = alternateSupportDistance + radialRing * radialStep;
        const center = {
          x: parentCenter.x + alternateDirection.x * distance,
          y: parentCenter.y + alternateDirection.y * distance,
        };
        addCandidate({
          x: center.x - size.width / 2,
          y: center.y - size.height / 2,
        }, directionIndex + 20, radialRing, Math.abs(degrees) * 4);
      }
    });
  }

  const idealPosition = {
    x: parentCenter.x + unitDirection.x * preferredDistance - size.width / 2,
    y: parentCenter.y + unitDirection.y * preferredDistance - size.height / 2,
  };
  nodes.forEach((node) => {
    if (node.id === parent.id) return;
    const rect = nodeRect(node, nodeSizes);
    if (Math.abs(unitDirection.x) >= Math.abs(unitDirection.y)) {
      addCandidate({
        x: idealPosition.x,
        y: rect.y + rect.height + gap.vertical,
      }, 1);
      addCandidate({
        x: idealPosition.x,
        y: rect.y - size.height - gap.vertical,
      }, -1, 0, gap.vertical * 4);
    } else {
      addCandidate({
        x: rect.x + rect.width + gap.vertical,
        y: idealPosition.y,
      }, -1);
      addCandidate({
        x: rect.x - size.width - gap.vertical,
        y: idealPosition.y,
      }, 1);
    }
  });

  if (usableBounds) {
    const xPositions = new Set([
      usableBounds.minX,
      usableBounds.maxX - size.width,
      idealPosition.x,
      parentCenter.x - size.width / 2,
    ]);
    const yPositions = new Set([
      usableBounds.minY,
      usableBounds.maxY - size.height,
      idealPosition.y,
      parentCenter.y - size.height / 2,
    ]);
    const nearbyRects = [...existingRects, ...viewportObstacles]
      .filter((rect) => rectNearBounds(
        rect,
        usableBounds,
        Math.max(size.width, size.height) + gap.horizontal
      ));

    nearbyRects.forEach((rect) => {
      xPositions.add(rect.x - size.width - gap.vertical);
      xPositions.add(rect.x + rect.width + gap.vertical);
      xPositions.add(rect.x);
      xPositions.add(rect.x + rect.width - size.width);
      yPositions.add(rect.y - size.height - gap.vertical);
      yPositions.add(rect.y + rect.height + gap.vertical);
      yPositions.add(rect.y);
      yPositions.add(rect.y + rect.height - size.height);
    });

    const nearestAxisPositions = (positions, axis, extent) => (
      [...positions]
        .filter(Number.isFinite)
        .filter((value) => (
          value >= usableBounds[axis === 'x' ? 'minX' : 'minY']
          && value + extent <= usableBounds[axis === 'x' ? 'maxX' : 'maxY']
        ))
        .sort((left, right) => (
          Math.abs(left + extent / 2 - parentCenter[axis])
          - Math.abs(right + extent / 2 - parentCenter[axis])
          || left - right
        ))
        .slice(0, MAX_FREE_SPACE_AXIS_CANDIDATES)
    );
    const candidateXs = nearestAxisPositions(xPositions, 'x', size.width);
    const candidateYs = nearestAxisPositions(yPositions, 'y', size.height);

    candidateXs.forEach((x, xIndex) => {
      candidateYs.forEach((y, yIndex) => {
        addCandidate(
          { x, y },
          100 + xIndex,
          100 + yIndex,
          gap.vertical / 4
        );
      });
    });
  }

  candidates.sort((left, right) => (
    left.score - right.score
    || Math.abs(left.lane) - Math.abs(right.lane)
    || right.lane - left.lane
    || left.radialRing - right.radialRing
  ));
  if (preferred) {
    const explicitCandidateIndex = candidates.findIndex((candidate) => (
      candidate.radialRing === 0
      && candidate.lane === 0
      && Math.abs(candidate.position.x - preferred.x) <= 0.001
      && Math.abs(candidate.position.y - preferred.y) <= 0.001
    ));
    if (explicitCandidateIndex > 0) {
      candidates.unshift(candidates.splice(explicitCandidateIndex, 1)[0]);
    }
  }

  const fitsUsableViewport = (rect) => (
    rectFitsUsableViewport(rect, usableBounds, viewportObstacles)
  );
  const parentRect = nodeRect(parent, nodeSizes);
  const routeNodeObstacles = nodes
    .filter((node) => node.id !== parent.id)
    .map((node) => nodeRect(node, nodeSizes));
  const relationshipRoutes = relationships.map((relationship) => {
    const edgeParent = nodesById.get(relationship.parentId);
    const edgeChild = nodesById.get(relationship.childId);
    if (!edgeParent || !edgeChild) return null;
    return routeBoundaryCurve(
      nodeRect(edgeParent, nodeSizes),
      nodeRect(edgeChild, nodeSizes)
    );
  }).filter(Boolean);

  let routedCandidateCount = 0;
  const evaluateCandidate = (plannedCandidate, allowRouting = true) => {
    const candidate = {
      x: plannedCandidate.position.x,
      y: plannedCandidate.position.y,
    };
    const candidateRect = { ...candidate, ...size };
    if (existingRects.some((rect) => topLeftRectsOverlap(candidateRect, rect, gap.vertical))) {
      return null;
    }

    const candidateCenter = {
      x: candidate.x + size.width / 2,
      y: candidate.y + size.height / 2,
    };
    const crossesNode = nodes.some((node) => (
      node.id !== parent.id
      && segmentCrossesRect(parentCenter, candidateCenter, nodeRect(node, nodeSizes), 2)
    ));
    const crossesEdge = relationships.some((relationship) => {
      const edgeParent = nodesById.get(relationship.parentId);
      const edgeChild = nodesById.get(relationship.childId);
      if (!edgeParent || !edgeChild) return false;
      return segmentsCross(
        parentCenter,
        candidateCenter,
        centerOfNode(edgeParent, nodeSizes),
        centerOfNode(edgeChild, nodeSizes)
      );
    });

    let routeLength = Math.hypot(
      candidateCenter.x - parentCenter.x,
      candidateCenter.y - parentCenter.y
    );
    let routeBends = 0;
    if (crossesNode || crossesEdge) {
      if (!allowRouting) return null;
      if (routedCandidateCount >= MAX_ROUTED_PLACEMENT_CANDIDATES) return null;
      routedCandidateCount += 1;
      const route = routeEdge({
        sourceRect: parentRect,
        targetRect: candidateRect,
        nodeObstacles: routeNodeObstacles,
        edgeObstacles: relationshipRoutes,
      });
      if (route.cost.nodeIntersections > 0 || route.cost.edgeCrossings > 0) return null;
      routeLength = route.cost.length;
      routeBends = route.cost.bends;
    }

    return {
      candidate,
      candidateRect,
      score: plannedCandidate.score
        + Math.max(0, routeLength - Math.hypot(
          candidateCenter.x - parentCenter.x,
          candidateCenter.y - parentCenter.y
        )) * 2
        + routeBends * 24,
    };
  };

  const chooseBestCandidatePass = (
    plannedCandidates,
    { allowEarlyExit, allowRouting }
  ) => {
    let best = null;
    for (const plannedCandidate of plannedCandidates) {
      if (
        allowEarlyExit
        && best
        && plannedCandidate.score > best.score + gap.horizontal * 2
      ) break;
      const evaluated = evaluateCandidate(plannedCandidate, allowRouting);
      if (!evaluated) continue;
      if (
        !best
        || evaluated.score < best.score
      ) {
        best = evaluated;
      }
    }
    return best;
  };
  const chooseBestCandidate = (plannedCandidates, allowEarlyExit = true) => {
    const directCandidate = chooseBestCandidatePass(plannedCandidates, {
      allowEarlyExit,
      allowRouting: false,
    });
    const bestPlannedScore = plannedCandidates[0]?.score ?? Infinity;
    if (
      directCandidate
      && directCandidate.score <= bestPlannedScore + gap.horizontal
    ) {
      return directCandidate;
    }
    const routingCandidates = directCandidate
      ? plannedCandidates.filter((candidate) => candidate.score < directCandidate.score)
      : plannedCandidates;
    const routedCandidate = chooseBestCandidatePass(routingCandidates, {
      allowEarlyExit,
      allowRouting: true,
    });
    if (!directCandidate) return routedCandidate;
    if (routedCandidate && routedCandidate.score < directCandidate.score) {
      return routedCandidate;
    }
    return directCandidate;
  };

  if (preferred) {
    const explicitCandidate = candidates.find((candidate) => (
      candidate.radialRing === 0
      && candidate.lane === 0
      && Math.abs(candidate.position.x - preferred.x) <= 0.001
      && Math.abs(candidate.position.y - preferred.y) <= 0.001
    ));
    const evaluated = explicitCandidate ? evaluateCandidate(explicitCandidate) : null;
    if (evaluated) {
      return {
        position: evaluated.candidate,
        fitsUsableViewport: fitsUsableViewport(evaluated.candidateRect),
      };
    }
  }

  const visibleCandidates = hasUsableViewport
    ? candidates.filter((candidate) => fitsUsableViewport({ ...candidate.position, ...size }))
    : candidates;
  const bestVisibleCandidate = chooseBestCandidate(visibleCandidates);
  if (bestVisibleCandidate) {
    return {
      position: bestVisibleCandidate.candidate,
      fitsUsableViewport: true,
    };
  }

  routedCandidateCount = 0;
  const fallbackCandidates = candidates.map((candidate) => ({
    ...candidate,
    score: candidate.score + viewportOverflowDistance(
      { ...candidate.position, ...size },
      usableBounds
    ) * 1000,
  })).sort((left, right) => left.score - right.score);
  const bestMapCandidate = chooseBestCandidate(fallbackCandidates);
  if (bestMapCandidate) {
    return {
      position: bestMapCandidate.candidate,
      fitsUsableViewport: !hasUsableViewport,
    };
  }

  throw new Error('Unable to place child without overlap');
}

export function planChildLayout({
  parentId,
  nodes,
  relationships,
  preferredPosition = null,
  size = DEFAULT_NODE_SIZE,
  gap = DEFAULT_LAYOUT_GAP,
  nodeSizes = null,
  usableViewport = null,
}) {
  const safeNodes = Array.isArray(nodes) ? nodes : [];
  const safeRelationships = Array.isArray(relationships) ? relationships : [];
  const nodesById = new Map(safeNodes.map((node) => [node.id, node]));
  const parent = nodesById.get(parentId);
  if (!parent) throw new Error(`Parent node ${parentId} does not exist`);

  const childIds = safeRelationships
    .filter((relationship) => relationship.parentId === parentId)
    .map((relationship) => relationship.childId);
  const children = childIds.map((id) => nodesById.get(id)).filter(Boolean);
  const parentSize = getRuntimeNodeSize(parent, nodeSizes);
  const childSize = {
    width: Math.max(1, finite(size.width, DEFAULT_NODE_SIZE.width)),
    height: Math.max(1, finite(size.height, DEFAULT_NODE_SIZE.height)),
  };

  const preferred = preferredPosition;
  const parentCenter = centerOfNode(parent, nodeSizes);
  const direction = preferred
    ? normalizeVector({
      x: finite(preferred.x, 0) + childSize.width / 2 - parentCenter.x,
      y: finite(preferred.y, 0) + childSize.height / 2 - parentCenter.y,
    })
    : inferChildDirection(parent, nodesById, safeRelationships, children, nodeSizes);

  const placement = findAvailablePosition({
    parent,
    preferred: preferred
      ? { x: finite(preferred.x, 0), y: finite(preferred.y, 0) }
      : null,
    direction,
    size: childSize,
    nodes: safeNodes,
    relationships: safeRelationships,
    gap,
    nodeSizes,
    usableViewport,
  });
  const { position } = placement;
  const childCenterX = position.x + childSize.width / 2;
  const parentCenterX = finite(parent.position?.x, 0) + parentSize.width / 2;
  const side = childCenterX < parentCenterX ? -1 : 1;

  const highestOrder = children.reduce(
    (highest, child) => Math.max(highest, finite(child?.layout?.order, -1)),
    -1
  );
  return {
    position,
    fitsUsableViewport: placement.fitsUsableViewport,
    layoutSize: childSize,
    layout: {
      side: side < 0 ? 'left' : 'right',
      order: Math.max(children.length, highestOrder + 1),
      pinned: false,
    },
  };
}

export function planChildPosition(options) {
  return planChildLayout(options).position;
}

export function buildCreateChildCommand({
  id,
  parentId,
  nodes,
  relationships,
  preferredPosition = null,
  size = DEFAULT_NODE_SIZE,
  nodeSizes = null,
  usableViewport = null,
  color = null,
  userId = null,
  creatorName = null,
}) {
  if (!id) throw new Error('A child node id is required');

  const planned = planChildLayout({
    parentId,
    nodes,
    relationships,
    preferredPosition,
    size,
    nodeSizes,
    usableViewport,
  });

  return {
    type: 'create-child',
    fitsUsableViewport: planned.fitsUsableViewport,
    node: {
      id,
      text: '',
      position: planned.position,
      layoutSize: planned.layoutSize,
      layout: planned.layout,
      color,
      userId,
      creatorName,
      isRoot: false,
      collapsedChildren: null,
      embeddedMindmapId: null,
      linkUrl: null,
      linkMeta: null,
      isTask: false,
      taskDone: false,
      emoji: null,
      attachment: null,
      isEmbeddedMindmapLink: false,
      autoFocus: true,
    },
    relationship: { parentId, childId: id },
  };
}

export function buildCreateTaskChildCommand(options) {
  const command = buildCreateChildCommand(options);
  return {
    ...command,
    type: 'create-task-child',
    node: {
      ...command.node,
      isTask: true,
      taskDone: false,
    },
  };
}

export function buildCreateAttachmentCommand({ attachment, ...options }) {
  if (!attachment?.id || !['image', 'markdown'].includes(attachment.kind)) {
    throw new Error('A stored image or Markdown attachment is required');
  }
  const command = buildCreateChildCommand(options);
  return {
    ...command,
    type: 'create-attachment-child',
    node: {
      ...command.node,
      text: attachment.filename,
      autoFocus: false,
      attachment: {
        assetId: attachment.id,
        kind: attachment.kind,
        filename: attachment.filename,
        contentType: attachment.contentType,
        byteSize: attachment.byteSize,
        width: attachment.width,
        height: attachment.height,
      },
    },
  };
}

export function buildCreateSiblingCommand({ selectedNodeId, ...options }) {
  const safeNodes = Array.isArray(options.nodes) ? options.nodes : [];
  const safeRelationships = Array.isArray(options.relationships) ? options.relationships : [];
  const selectedNode = safeNodes.find((node) => node.id === selectedNodeId);
  if (!selectedNode) throw new Error(`Selected node ${selectedNodeId} does not exist`);

  const parentId = findParentId(selectedNodeId, safeRelationships);
  if (!parentId) throw new Error('The root node cannot have a sibling');

  const command = buildCreateChildCommand({
    ...options,
    parentId,
    nodes: safeNodes,
    relationships: safeRelationships,
    color: options.color ?? selectedNode.color ?? null,
  });

  return { ...command, type: 'create-sibling' };
}

export function buildCreateLinkedMapCommand({
  linkedMindmapId,
  linkedMindmapUrl,
  title = 'New Map',
  ...options
}) {
  if (!linkedMindmapId) throw new Error('A linked mind map id is required');
  if (!linkedMindmapUrl) throw new Error('A linked mind map URL is required');

  const command = buildCreateChildCommand(options);
  const displayTitle = String(title || '').trim() || 'New Map';
  return {
    ...command,
    type: 'create-linked-map',
    node: {
      ...command.node,
      text: displayTitle,
      autoFocus: false,
      embeddedMindmapId: linkedMindmapId,
      isEmbeddedMindmapLink: true,
      linkUrl: linkedMindmapUrl,
      linkMeta: {
        url: linkedMindmapUrl,
        title: displayTitle,
        description: null,
        image: null,
        siteName: 'Mindmappi',
      },
    },
  };
}

export function buildDeleteSubtreeCommand({
  nodeId,
  rootId,
  nodes,
  relationships,
  actorUserId = null,
}) {
  const safeNodes = Array.isArray(nodes) ? nodes : [];
  const safeRelationships = Array.isArray(relationships) ? relationships : [];
  const nodesById = new Map(safeNodes.map((node) => [node.id, node]));
  const node = nodesById.get(nodeId);
  if (!node) throw new Error(`Node ${nodeId} does not exist`);
  if (nodeId === rootId || node.isRoot) throw new Error('The root node cannot be deleted');

  const nodeIds = getSubtreeIds(nodeId, safeRelationships);
  const subtreeNodes = nodeIds.map((id) => nodesById.get(id));
  if (subtreeNodes.some((candidate) => !candidate)) {
    throw new Error('The branch contains a relationship to a missing node');
  }
  if (nodeIds.includes(rootId) || subtreeNodes.some((candidate) => candidate.isRoot)) {
    throw new Error('The branch is cyclic and would delete the root node');
  }

  if (actorUserId) {
    const foreignNode = subtreeNodes.find(
      (candidate) => candidate.userId && candidate.userId !== actorUserId
    );
    if (foreignNode) {
      throw new Error('This branch contains nodes created by another collaborator');
    }
  }

  const parentRelationships = safeRelationships.filter(
    (relationship) => relationship.childId === nodeId
  );
  if (parentRelationships.length !== 1) {
    throw new Error('The branch does not have exactly one parent relationship');
  }

  const deletingIds = new Set(nodeIds);
  const removedRelationships = safeRelationships.filter((relationship) => (
    deletingIds.has(relationship.parentId) || deletingIds.has(relationship.childId)
  ));
  const parentId = parentRelationships[0].parentId;

  return {
    type: 'delete-subtree',
    nodeId,
    nodeIds,
    parentId,
    nextSelectedId: parentId || rootId || null,
    removedRelationships,
  };
}
